import { PaperSize, PosPrintData, PosPrintOptions, SizeOptions } from "@3ksy/electron-pos-printer";
import {
	Location,
	OrderInput,
	OrderProductInput,
	OrderService,
	Shop,
} from "../gql/graphql";
import { getDate, getTime } from "./date.util";
import { calculateOrderTotal } from "./order.utils";
import { formatMoney } from "./money";

/**
 * The location's bill settings as the till needs them
 * (openspec/changes/location-bill-settings). Absent: today's plain receipt.
 */
export interface ReceiptSettings {
	currency: string;
	printedName: string;
	gstin?: string | null;
	headerNote?: string | null;
	footerText: string;
	receiptPaper: string;
	show: {
		address: boolean;
		phone: boolean;
		email: boolean;
		gstin: boolean;
		customerPhone: boolean;
		paymentMethod: boolean;
		staff: boolean;
		discountColumn: boolean;
	};
}

/** What the till prints from beyond the order: the location's settings and logo, staff names by user id, the customer's phone. */
export interface ReceiptBill {
	settings?: ReceiptSettings | null;
	logo?: string | null;
	staffNames?: Record<string, string>;
	customerPhone?: string | null;
}

const ALL_SHOWN: ReceiptSettings["show"] = {
	address: true, phone: true, email: true, gstin: true, customerPhone: true, paymentMethod: true, staff: true, discountColumn: true,
};

/**
 * electron-pos-printer writes every text line and table cell with innerHTML,
 * in a window that has Node enabled. A product, customer or shop name holding
 * markup would therefore run on the till. Everything passed to it is escaped.
 */
const esc = (value: unknown): string =>
	String(value ?? "").replace(/[&<>"']/g, (c) => (
		{ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>
	)[c]);
const lines = (value: string) => esc(value).replace(/\r?\n/g, "<br>");

const getAddress = (location?: Location | null): string => {
	const a = location?.address;
	return [a?.street, a?.landmark, a?.city]
		.map((part) => (part ?? "").trim())
		.filter((part) => part && part !== "-")
		.join(", ");
};

const getInvoiceRow = (value: string): PosPrintData => {
	return {
		type: "text",
		value: esc(value),
		style: { textAlign: "left", fontSize: "15px" },
	};
};

const centred = (value: string, fontSize = "12px", extra: Record<string, string> = {}): PosPrintData => ({
	type: "text",
	value,
	style: { textAlign: "center", fontSize, ...extra },
	position: "center",
});

/**
 * The till's paper: the location's setting. Only when the settings could not be
 * read does it fall back to this device's old `page-size`, so a till set up for
 * 58 mm before bill settings existed does not switch paper mid-outage.
 */
const paperSize = (setting?: string): PaperSize | SizeOptions => {
	if (setting === "THERMAL_58") return "58mm";
	if (setting === "THERMAL_80") return "80mm";
	if (setting === "A4") return { width: 794, height: 1123 };
	return (localStorage.getItem("page-size") || "80mm") as PaperSize | SizeOptions;
};

/**
 * Prints the till receipt for an order at the ACTIVE location (it used to
 * print the shop's first location's address, whatever branch made the bill),
 * styled by that location's bill settings and logo when given.
 */
export const printPOSReceipt = async (
	orderData: OrderInput,
	shop: Shop,
	locationId?: string,
	bill?: ReceiptBill
) => {
	if (window?.electronAPI?.isDesktop) {
		const settings = bill?.settings ?? null;
		const show = { ...ALL_SHOWN, ...(settings?.show ?? {}) };
		// Never another branch's details: a shop's only location may stand in, nothing else.
		const locations = shop?.location ?? [];
		const location = locations.find((l) => l?._id === locationId) ?? (locations.length === 1 ? locations[0] : undefined);
		const money = (amount: number) => formatMoney(amount, settings?.currency);
		const amount = (n: number) => formatMoney(n, settings?.currency).replace(/[^\d.,-]/g, "");

		const margin = localStorage.getItem("printer-margin") || "0 0 0 0";
		const preview = localStorage.getItem("preview") ? true : false;
		const options: PosPrintOptions = {
			copies: 1,
			boolean: true,
			preview,
			margin,
			pageSize: paperSize(settings?.receiptPaper),
			printerName: localStorage.getItem("printer-name") || "Default",
		};

		const staffName = (userId?: string | null) => (show.staff && userId ? bill?.staffNames?.[userId] : undefined);
		const row = (item: { name?: string | null; price: number; quantity: number; discountAmount?: number | null; stylistUserId?: string | null }) => {
			const staff = staffName(item.stylistUserId);
			const cells = [
				esc(item.name) + (staff ? `<br><span style="font-size:10px">${esc(staff)}</span>` : ""),
				amount(item.price),
				esc(item.quantity),
				...(show.discountColumn ? [amount(item.discountAmount ?? 0)] : []),
				amount(item.price * item.quantity - (item.discountAmount ?? 0)),
			];
			return cells;
		};
		const productsTable = orderData.products.map((item: OrderProductInput) => row(item));
		const servicesTable = orderData.services.map((item: OrderService) => row(item as any));

		const footers: PosPrintData[] = calculateOrderTotal(orderData)
			.filter((item) => show.discountColumn || item.label !== "Discount")
			.map((item) => getInvoiceRow(`${item.label}     : ${money(item.value)}`));

		const logo = bill?.logo && /^data:image\/(png|jpeg|webp);base64,/.test(bill.logo) ? bill.logo : null;
		const address = getAddress(location);
		const data: PosPrintData[] = [
			...(logo ? [{ type: "image", url: logo, position: "center", width: "160px" } as PosPrintData] : []),
			centred(esc(settings?.printedName || shop.name), "24px"),
			...(show.address && address ? [centred(esc(address), "12px", { marginRight: "10px", marginLeft: "10px" })] : []),
			...(show.phone && location?.phone ? [centred(`Mobile: ${esc(location.phone)}`)] : []),
			...(show.email && location?.email ? [centred(`Email: ${esc(location.email)}`)] : []),
			...(show.gstin && settings?.gstin ? [centred(`GSTIN: ${esc(settings.gstin)}`)] : []),
			...(settings?.headerNote ? [centred(lines(settings.headerNote))] : []),
			centred("Invoice", "16px", { marginTop: "10px", fontStyle: "bold" }),
			centred("======================================="),
			getInvoiceRow(`Order No     : ${orderData.sequenceNumber}`),
			getInvoiceRow(`Order Date   : ${getDate(orderData.createdAt)}`),
			getInvoiceRow(`Order Time   : ${getTime(orderData.createdAt)}`),
			...(show.customerPhone && bill?.customerPhone
				? [getInvoiceRow(`Customer     : ${[orderData.customerName, bill.customerPhone].filter(Boolean).join(", ")}`)]
				: []),
			...(show.paymentMethod && orderData.paymentMethod ? [getInvoiceRow(`Payment      : ${orderData.paymentMethod}`)] : []),
			centred("======================================="),
			{
				type: "table",
				tableHeader: ["Item", "Price", "Qty", ...(show.discountColumn ? ["Disc"] : []), "Total"],
				tableBody: [
					...productsTable,
					...servicesTable
				],
				tableHeaderStyle: { backgroundColor: "white", color: "black" },
				tableBodyStyle: { border: "0.5px solid #ddd" },
				tableFooterStyle: { backgroundColor: "#000", color: "white" },
			},
			centred("======================================="),
			...footers,
			...(settings?.footerText ? [centred(lines(settings.footerText), "12px", { marginTop: "10px" })] : []),
		];
		return await window?.electronAPI?.printPOS(data, options);
	}
};
